import { useCallback, useEffect, useRef, useState } from "react"
import { InvoiceApi } from "../../apis"

// Frontend poll of GET /inv/invoice/:id. The de-payment-service confirms the
// Golomt payment in the background (polls the bank every ~10s for up to 5 min),
// then flips the invoice row's paymentStatus / invoiceStatus. We poll the
// invoice until that flip is visible, with a matching timeout.
const POLL_INTERVAL_MS = 3000
const POLL_TIMEOUT_MS = 5 * 60 * 1000

// Mirrors detail-form's isPaid: CLOSED = fully paid, DIVIDED = partially paid.
function isPaidInvoice(invoice: any): boolean {
  return (
    invoice?.paymentStatus === "CLOSED" ||
    invoice?.invoiceStatus === "CLOSED" ||
    invoice?.paymentStatus === "DIVIDED"
  )
}

type StartArgs = {
  invoiceId: string
  popup: Window | null
  onPaid: () => void
  onPending: () => void
}

export function usePaymentPolling() {
  const [polling, setPolling] = useState<boolean>(false)

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const popupRef = useRef<Window | null>(null)
  const activeRef = useRef<boolean>(false)
  const busyRef = useRef<boolean>(false)
  const deadlineRef = useRef<number>(0)
  const forceFinalRef = useRef<boolean>(false)
  const tickRef = useRef<(() => void) | null>(null)

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    activeRef.current = false
    busyRef.current = false
    forceFinalRef.current = false
    tickRef.current = null
    setPolling(false)
  }, [])

  const closePopup = useCallback(() => {
    try {
      popupRef.current?.close()
    } catch {
      // cross-origin popup — close may throw, ignore
    }
    popupRef.current = null
  }, [])

  const start = useCallback(
    ({ invoiceId, popup, onPaid, onPending }: StartArgs) => {
      // Guard against a second concurrent poll session.
      if (activeRef.current) return

      activeRef.current = true
      popupRef.current = popup
      deadlineRef.current = Date.now() + POLL_TIMEOUT_MS
      setPolling(true)

      const finishPaid = () => {
        stop()
        closePopup()
        onPaid()
      }

      const finishPending = () => {
        stop()
        closePopup()
        onPending()
      }

      const tick = async () => {
        if (!activeRef.current || busyRef.current) return
        busyRef.current = true

        try {
          const popupClosed = !!(popupRef.current && popupRef.current.closed)
          const timedOut = Date.now() > deadlineRef.current
          const finalAttempt = forceFinalRef.current || popupClosed || timedOut

          try {
            const invoice = await InvoiceApi.get(invoiceId)
            if (isPaidInvoice(invoice)) {
              finishPaid()
              return
            }
          } catch {
            // Transient fetch error — keep polling unless this was the last try.
          }

          if (finalAttempt) finishPending()
        } finally {
          busyRef.current = false
        }
      }

      tickRef.current = () => {
        void tick()
      }

      timerRef.current = setInterval(() => {
        void tick()
      }, POLL_INTERVAL_MS)
    },
    [stop, closePopup],
  )

  // Force a single immediate final status check, then stop. Used when a
  // component-level surface (e.g. the QPAY QR modal, which has no popup window
  // to observe) is closed manually: we check once more before giving up, so a
  // just-completed payment is still detected. A manual close is not a failure.
  const finalize = useCallback(() => {
    if (!activeRef.current) return
    forceFinalRef.current = true
    tickRef.current?.()
  }, [])

  // Clean up any running interval if the component unmounts mid-poll.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      activeRef.current = false
      busyRef.current = false
    }
  }, [])

  return { polling, start, stop, finalize }
}
